import React, { useState, useEffect } from 'react';

interface DateTimePickerProps {
    startDateTime: string;
    endDateTime: string;
    onDateRangeChange: (start: string, end: string) => void;
    className?: string;
}

/**
 * DateTimePicker component that provides date range selection with apply button
 * Adapted from project314-frontend but using logwatch styling
 */
const DateTimePicker: React.FC<DateTimePickerProps> = ({ startDateTime, endDateTime, onDateRangeChange, className = '' }) => {
    // Convert ISO string to date input format (YYYY-MM-DD)
    const formatDateForInput = (dateString: string) => {
        try {
            if (!dateString) return '';
            const date = new Date(dateString);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (e) {
            console.error('Error formatting date:', e);
            return '';
        }
    };

    // Convert ISO string to time input format (HH:MM)
    const formatTimeForInput = (dateString: string) => {
        try {
            if (!dateString) return '';
            const date = new Date(dateString);
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${hours}:${minutes}`;
        } catch (e) {
            console.error('Error formatting time:', e);
            return '';
        }
    };

    // State for the input display values - separated into date and time
    const [startDateValue, setStartDateValue] = useState(formatDateForInput(startDateTime));
    const [startTimeValue, setStartTimeValue] = useState(formatTimeForInput(startDateTime));
    const [endDateValue, setEndDateValue] = useState(formatDateForInput(endDateTime));
    const [endTimeValue, setEndTimeValue] = useState(formatTimeForInput(endDateTime));

    // Update local state when props change
    useEffect(() => {
        setStartDateValue(formatDateForInput(startDateTime));
        setStartTimeValue(formatTimeForInput(startDateTime));
        setEndDateValue(formatDateForInput(endDateTime));
        setEndTimeValue(formatTimeForInput(endDateTime));
    }, [startDateTime, endDateTime]);

    const handleApplyCustomRange = () => {
        try {
            if (startDateValue && startTimeValue && endDateValue && endTimeValue) {
                const startDateParts = startDateValue.split('-');
                const startTimeParts = startTimeValue.split(':');
                const endDateParts = endDateValue.split('-');
                const endTimeParts = endTimeValue.split(':');

                if (startDateParts.length !== 3 || startTimeParts.length !== 2 || endDateParts.length !== 3 || endTimeParts.length !== 2) {
                    console.error('Invalid date format in inputs');
                    return;
                }

                // Create date objects using local time
                const startDateTime = new Date(
                    parseInt(startDateParts[0]),
                    parseInt(startDateParts[1]) - 1,
                    parseInt(startDateParts[2]),
                    parseInt(startTimeParts[0]),
                    parseInt(startTimeParts[1])
                );

                const endDateTime = new Date(
                    parseInt(endDateParts[0]),
                    parseInt(endDateParts[1]) - 1,
                    parseInt(endDateParts[2]),
                    parseInt(endTimeParts[0]),
                    parseInt(endTimeParts[1])
                );

                if (!isNaN(startDateTime.getTime()) && !isNaN(endDateTime.getTime())) {
                    // Convert to ISO strings for consistency
                    const startISOString = startDateTime.toISOString();
                    const endISOString = endDateTime.toISOString();

                    // Pass the ISO strings to the parent component
                    onDateRangeChange(startISOString, endISOString);
                } else {
                    console.error('Invalid date format in Apply action');
                }
            }
        } catch (error) {
            console.error('Error in Apply action:', error);
        }
    };

    return (
        <div className={`space-y-3 ${className}`}>
            {/* Start Date/Time */}
            <div className="grid grid-cols-2 gap-2">
                <div>
                    <label className="form-label text-xs">Start Date</label>
                    <input
                        type="date"
                        className="form-control text-sm"
                        value={startDateValue}
                        onChange={(e) => setStartDateValue(e.target.value)}
                    />
                </div>
                <div>
                    <label className="form-label text-xs">Start Time</label>
                    <input
                        type="time"
                        className="form-control text-sm"
                        value={startTimeValue}
                        onChange={(e) => setStartTimeValue(e.target.value)}
                    />
                </div>
            </div>

            {/* End Date/Time */}
            <div className="grid grid-cols-2 gap-2">
                <div>
                    <label className="form-label text-xs">End Date</label>
                    <input
                        type="date"
                        className="form-control text-sm"
                        value={endDateValue}
                        onChange={(e) => setEndDateValue(e.target.value)}
                    />
                </div>
                <div>
                    <label className="form-label text-xs">End Time</label>
                    <input
                        type="time"
                        className="form-control text-sm"
                        value={endTimeValue}
                        onChange={(e) => setEndTimeValue(e.target.value)}
                    />
                </div>
            </div>

            {/* Apply Button */}
            <button
                onClick={handleApplyCustomRange}
                className="btn btn-primary w-full text-sm"
                disabled={!startDateValue || !startTimeValue || !endDateValue || !endTimeValue}
            >
                <i className="fas fa-check mr-2"></i>
                Apply Date Range
            </button>
        </div>
    );
};

export default DateTimePicker;
